import classNames from 'classnames';
import { useState, Dispatch, SetStateAction } from 'react';
import { motion } from 'framer-motion';

import { useMediaQuery } from 'react-responsive';
import { navMenuIcons } from '../../../constants/icons';
import SocialBar, {
  Props as SocialBarProps
} from '../SocialBar';
import Icon from '../../Atoms/Icon';
import Badge from '../../Atoms/Badge';

type SubNavItemProps = {
  label: string;
  url: string;
  icon:
    | 'windmill'
    | 'plug'
    | 'code'
    | 'conveyor'
    | 'gear'
    | 'helmet'
    | 'chart'
    | 'clipboard';
};

type NavItemProps = {
  theme?: string;
  label: string;
  url: string;
  subNav?: SubNavItemProps[];
};

type SubMenuItem = {
  label: string;
  url: string;
  badge?: string;
};

type MenuItemProps = {
  label: string;
  url: string;
  id: string;
  subMenu?: SubMenuItem[];
  setActiveItem?: Dispatch<SetStateAction<string>>;
  activeItem?: string;
};

type MenuSecondaryItemProps = {
  label: string;
  url: string;
};

interface Props {
  theme?: 'dark' | 'light';
  nav: NavItemProps[];
  menu: MenuItemProps[];
  menuSecondary: MenuSecondaryItemProps[];
  social: SocialBarProps['socialItems'];
}

function SubNavItem({ label, url, icon }: SubNavItemProps) {
  return (
    <li>
      <a
        href={url}
        className="flex items-center font-camphor text-sl-blue transition-colors hover:text-sl-indigo"
      >
        <span className="mr-6 inline-flex shrink-0 basis-[45px] justify-center">
          {navMenuIcons[icon as keyof typeof navMenuIcons]}
        </span>
        {label}
      </a>
    </li>
  );
}

function SubNav({ items }: { items: SubNavItemProps[] }) {
  return (
    <div className="absolute left-1/2 z-50 -ml-[340px] max-h-0 w-[680px] overflow-hidden transition-all group-hover:max-h-screen">
      <div className="p-[40px] opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <span className="absolute left-1/2 top-0 -ml-[20px] border-l-[20px] border-r-[20px] border-b-[20px] border-l-transparent border-r-transparent border-b-white" />
        <ul className="-mt-[20px] grid grid-cols-2 gap-x-3 gap-y-10 overflow-hidden rounded-xl bg-white px-9 py-11 text-lg font-normal leading-tight tracking-tighter shadow-light-blue-xs">
          {items.map((subNavItem) => (
            <SubNavItem
              {...subNavItem}
              key={subNavItem.label}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function NavItem({
  theme,
  label,
  url,
  subNav
}: NavItemProps) {
  return (
    <li className="group relative">
      <a
        href={url}
        className={classNames(
          'transition-colors',
          {
            'text-sl-blue hover:text-sl-indigo':
              theme === 'dark'
          },
          {
            'text-white hover:text-sl-yellow':
              theme === 'light'
          }
        )}
      >
        {label}
      </a>
      {subNav && <SubNav items={subNav} />}
    </li>
  );
}

function Nav({
  items,
  theme
}: {
  items: NavItemProps[];
  theme: string;
}) {
  return (
    <ul className="flex items-center space-x-7 font-circular text-base font-medium">
      {items.map((navItem: NavItemProps) => (
        <NavItem
          {...navItem}
          theme={theme}
          key={navItem.label}
        />
      ))}
    </ul>
  );
}

function MenuItem({
  label,
  url,
  id,
  subMenu,
  activeItem,
  setActiveItem
}: MenuItemProps) {
  const isTouchDevice = useMediaQuery({
    query: '(hover: none) and (pointer: coarse)'
  });
  const handleItemHover = (): void => {
    setActiveItem?.(id);
  };

  const handleItemClick = (
    e: React.SyntheticEvent
  ): void => {
    e.preventDefault();
    if (!activeItem || activeItem !== id) {
      setActiveItem?.(id);
    } else {
      setActiveItem?.('');
    }
  };

  const variants = {
    open: { opacity: 1, height: 'auto' },
    closed: { opacity: 0, height: 0 }
  };
  return (
    <li className="pt-2">
      <a
        href={url}
        className="mb-2 flex items-center justify-between text-white transition-colors md:hover:text-[hsla(0,0%,100%,.5)]"
        onMouseEnter={
          !isTouchDevice && subMenu
            ? handleItemHover
            : undefined
        }
        onClick={
          isTouchDevice && subMenu
            ? handleItemClick
            : undefined
        }
      >
        {label}

        {subMenu && (
          <Icon
            icon="chevron-right"
            className={classNames(
              'ml-3 rotate-90 text-xl leading-none transition-transform md:rotate-0 md:text-[0.6em]',
              { 'rotate-[270deg]': activeItem === id }
            )}
          />
        )}
      </a>

      {subMenu && (
        <motion.div
          className="-mx-5 overflow-hidden bg-white px-5 md:!hidden"
          animate={activeItem === id ? 'open' : 'closed'}
          variants={variants}
        >
          <ul className="space-y-6 py-8 text-lg">
            {subMenu.map((item) => (
              <li key={subMenu.indexOf(item)}>
                <a
                  href={item.url}
                  className="flex items-center text-sl-blue"
                >
                  {item.badge && (
                    <Badge
                      className="mr-3 h-10 shrink-0 basis-10 justify-center !p-1"
                      color="indigo-light"
                      text={
                        <span>
                          {item.badge.split(' ')[0]}
                        </span>
                      }
                    />
                  )}
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </li>
  );
}

function Menu({
  items,
  activeItem,
  setActiveItem
}: {
  items: MenuItemProps[];
  setActiveItem: Dispatch<SetStateAction<string>>;
  activeItem: string;
}) {
  return (
    <ul className="text-2xl lg:text-3xl">
      {items.map((menuItem) => (
        <MenuItem
          {...menuItem}
          key={menuItem.label}
          setActiveItem={setActiveItem}
          activeItem={activeItem}
        />
      ))}
    </ul>
  );
}

function MenuSecondary({
  menu
}: {
  menu: MenuSecondaryItemProps[];
}) {
  return (
    <ul className="text-xl lg:text-2xl">
      {menu.map((item) => (
        <li className="mb-4 last:mb-0" key={item.label}>
          <a
            href={item.url}
            className="text-white opacity-50 transition-opacity hover:opacity-100"
          >
            {item.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

function MenuLogo() {
  return (
    <a
      href="/"
      className="flex min-h-[40px] w-28 items-center md:min-h-[48px] md:w-32 lg:w-36"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="140"
        height="40"
        viewBox="0 0 140 40"
      >
        <path
          fill="#fff"
          d="M14.626 5.658V1.384a.584.584 0 00-.61-.583C6.428 1.11.333 7.203.02 14.788a.587.587 0 00.586.61h4.274c0-5.38 4.364-9.74 9.747-9.74M14.626 34.877v4.274c0 .332.278.597.61.583 7.589-.31 13.684-6.403 13.997-13.986a.587.587 0 00-.587-.61h-4.274c0 5.379-4.363 9.739-9.746 9.739M14.626 10.528a4.871 4.871 0 00-4.873 4.87h4.873v-4.87zM14.626 30.007a4.872 4.872 0 004.873-4.87h-4.873v4.87z"
        />
        <path
          fill="#fff"
          d="M14.626 30.007a4.872 4.872 0 00-4.829 4.211.585.585 0 00.582.659h4.247v-4.87zM19.499 25.137h4.873c0-5.38-4.364-9.74-9.746-9.74v4.87a4.87 4.87 0 014.873 4.87M9.753 15.398H4.88c0 5.38 4.364 9.74 9.745 9.74v-4.87a4.87 4.87 0 01-4.872-4.87M14.625 10.527v-4.87h4.247c.353 0 .63.31.582.66a4.872 4.872 0 01-4.829 4.21zM38.99 27.589c0-.102 0-.235.067-.336l1.142-2.116c.135-.268.404-.37.639-.37.135 0 .236 0 .37.068.672.404 2.386 1.108 4.604 1.108 1.109 0 2.622-.47 2.622-1.276 0-2.284-9.343-1.982-9.343-8.363 0-3.56 2.486-5.744 6.956-5.744 2.117 0 4.705.84 5.747 1.31a.959.959 0 01.403.605c0 .101 0 .235-.033.37l-1.076 2.485c-.1.201-.436.47-.706.47l-.37-.1c-.839-.236-2.855-.706-3.865-.706-2.184 0-2.352 1.075-2.352 1.31 0 2.284 9.344 2.418 9.344 8.329 0 3.46-4.067 5.407-6.856 5.407-2.185 0-4.605-.402-6.923-1.813-.135-.068-.37-.404-.37-.638M64.934 22.753v6.415a.689.689 0 01-.706.705h-3.293c-.437 0-.706-.302-.706-.705v-6.415L53.54 11.67c0-.538.336-.873.806-.873h3.194c.604 0 .94.335 1.075.739l3.966 6.481 3.965-6.481c.134-.404.47-.739 1.075-.739h3.193c.471 0 .806.335.806.873l-6.687 11.083zM73.132 11.266c0-.403.336-.671.874-.671l11.056 7.12v-5.912c0-.57.404-1.007 1.009-1.007h2.688a.99.99 0 011.009 1.007v17.364a.69.69 0 01-.706.705h-3.294c-.437 0-.706-.302-.706-.705v-5.81l-7.225-4.736v10.546a.69.69 0 01-.706.706h-3.293c-.437 0-.706-.303-.706-.706v-17.9zM92.791 27.589c0-.102 0-.235.067-.336l1.143-2.116c.135-.268.404-.37.64-.37.133 0 .234 0 .368.068.672.404 2.387 1.108 4.604 1.108 1.109 0 2.622-.47 2.622-1.276 0-2.284-9.343-1.982-9.343-8.363 0-3.56 2.486-5.744 6.956-5.744 2.118 0 4.706.84 5.747 1.31a.956.956 0 01.403.605c0 .101 0 .235-.033.37l-1.075 2.485c-.102.201-.437.47-.706.47l-.369-.1c-.841-.236-2.857-.706-3.866-.706-2.184 0-2.353 1.075-2.353 1.31 0 2.284 9.344 2.418 9.344 8.329 0 3.46-4.067 5.407-6.856 5.407-2.184 0-4.604-.402-6.923-1.813-.135-.068-.37-.404-.37-.638M120.68 18.218a.69.69 0 01.707.706v3.022c0 .437-.303.706-.707.706h-6.452v2.788h8.2c.403 0 .705.301.705.704v3.024c0 .436-.302.704-.705.704h-12.2a.689.689 0 01-.705-.704V11.502c0-.404.302-.706.705-.706h12.2c.403 0 .705.302.705.706v3.123c0 .437-.302.705-.705.705h-8.2v2.888h6.452zM126.16 11.501c0-.403.27-.705.706-.705h3.294c.403 0 .706.302.706.705v13.971h8.199c.404 0 .706.302.706.706v2.99c0 .435-.302.703-.706.703h-12.199c-.436 0-.706-.301-.706-.704V11.501z"
        />
      </svg>
    </a>
  );
}

function HeaderNav({
  nav,
  menu,
  menuSecondary,
  social,
  theme = 'dark'
}: Props) {
  const [menuIsOpen, setmenuIsOpen] =
    useState<boolean>(false);

  const [activeSubMenu, setActiveSubMenu] =
    useState<string>('');

  const handleMenuOpen = (): void => {
    setmenuIsOpen(true);

    document.body.style.overflow = 'hidden';
  };

  const handleMenuClose = (): void => {
    setmenuIsOpen(false);

    document.body.style.overflow = 'initial';
  };

  const subMenuVariants = {
    open: { opacity: 1, display: 'block' },
    collapsed: { opacity: 0, display: 'none' }
  };

  const subMenuTransition = {
    duration: 1.5,
    ease: [0.04, 0.62, 0.23, 0.98]
  };

  return (
    <div>
      <nav className="flex items-center space-x-7">
        <div className="hidden lg:inline-flex">
          <Nav items={nav} theme={theme} />
        </div>
        <button
          onClick={handleMenuOpen}
          type="button"
          className={classNames(
            'inline-flex items-center rounded-xl px-4 py-3 font-circular text-xs font-medium  transition-colors md:text-base',
            {
              'bg-[rgba(35,39,91,.1)] text-sl-blue hover:bg-[rgba(35,39,91,.15)]':
                theme === 'dark'
            },
            {
              'bg-[rgba(255,255,255,.1)] text-white hover:bg-[rgba(255,255,255,.15)]':
                theme === 'light'
            }
          )}
        >
          Menu
          <span className="relative ml-4 block h-[9px] w-[20px]">
            <span className="absolute left-0 top-0 h-[2px] w-[20px] bg-current" />
            <span className="absolute bottom-0 right-0 h-[2px] w-[10px] bg-current" />
          </span>
        </button>
      </nav>
      <div
        className={classNames(
          'absolute top-0 left-0 z-50 h-screen w-full bg-sl-blue',
          { flex: menuIsOpen },
          { hidden: !menuIsOpen }
        )}
      >
        <aside className="flex shrink-0 grow overflow-y-auto md:grow-0 md:basis-[330px] lg:basis-[400px]">
          <div className="flex grow flex-col justify-between">
            <div className="flex justify-between py-4 px-5 text-white md:py-6 md:px-7">
              <MenuLogo />
              <button
                onClick={handleMenuClose}
                type="button"
                className="inline-flex items-center rounded-xl bg-white px-4 py-3 font-circular text-xs font-medium text-sl-blue md:hidden md:text-base"
              >
                Menu
                <span className="relative ml-4 block h-[9px] w-[20px]">
                  <Icon
                    icon="x-mark"
                    className="absolute left-0 top-0 -mt-[7px] text-2xl leading-none"
                  />
                </span>
              </button>
            </div>
            <div className="px-5 py-7 md:px-14">
              <p className="mb-7 text-[10px] uppercase tracking-widest text-white">
                navigeren
              </p>
              <Menu
                items={menu}
                setActiveItem={setActiveSubMenu}
                activeItem={activeSubMenu}
              />
            </div>
            <div className="px-5 pt-7 pb-28 md:px-14 lg:pb-10">
              <div className="mb-6">
                <MenuSecondary menu={menuSecondary} />
              </div>
              <SocialBar
                socialItems={social}
                iconClassName="text-white"
              />
            </div>
          </div>
        </aside>
        <div className="hidden grow overflow-y-auto bg-white px-5 pt-6 pb-10 md:block md:px-7">
          <div className="mb-7 flex justify-end">
            <button
              onClick={handleMenuClose}
              type="button"
              className="inline-flex items-center justify-between rounded-xl bg-[rgba(35,39,91,.1)] px-4 py-3 font-circular text-xs font-medium text-sl-blue transition-colors hover:bg-[rgba(35,39,91,.15)] md:text-base"
            >
              Sluit
              <span className="relative ml-4 block h-[9px] w-[20px]">
                <Icon
                  icon="x-mark"
                  className="absolute left-0 top-0 -mt-[7px] text-2xl leading-none"
                />
              </span>
            </button>
          </div>
          <div className="pt-7 lg:px-10 xl:px-20">
            {menu.map(
              (item) =>
                item.subMenu && (
                  <motion.div
                    key={item.label}
                    variants={subMenuVariants}
                    transition={subMenuTransition}
                    animate={
                      activeSubMenu === item.id
                        ? 'open'
                        : 'collapsed'
                    }
                  >
                    <p className="mb-10 text-xs uppercase tracking-widest text-sl-blue">
                      werkgebieden
                    </p>
                    <ul className="grid gap-6 text-xl lg:grid-cols-2 lg:gap-x-10 lg:gap-y-14 xl:text-2xl">
                      {item.subMenu.map((subMenuItem) => (
                        <li
                          key={item.subMenu?.indexOf(
                            subMenuItem
                          )}
                        >
                          <a
                            href={subMenuItem.url}
                            className="group flex flex-col items-start text-sl-blue transition-colors hover:text-sl-indigo"
                          >
                            <span>{subMenuItem.label}</span>
                            {subMenuItem.badge && (
                              <Badge
                                rounded="lg"
                                size="sm"
                                color="indigo-light"
                                className="mt-3 group-hover:bg-sl-indigo group-hover:bg-opacity-100 group-hover:text-white"
                                text={subMenuItem.badge}
                              />
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeaderNav;
